import { useLoaderData } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { getProductCategoryUrl } from '~/modules/product/util/get-product-category-url';
import { getProductRangeUrl } from '~/modules/product/util/get-product-range-url';
import type { loader } from '~/routes/_app.product.$urlSegment';
import {
    type NumberedAccordionRow,
    type DetailTile,
    stripHtml,
} from '~/ui-rocco/pages/detail-shared';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import { useProductEnquiryModal } from '../../hooks/use-product-enquiry-modal';
import { ProductEnquiryPrompt } from '../ProductEnquiryPrompt';
import { groupApprovedFacetOptions } from './group-facet-options';
import {
    PRODUCT_DETAILS_ANCHOR_ID,
    ProductDetailsRowContent,
    type ProductInfoItem,
} from './ProductDetailsRowContent';
import { SpecsDimensionsRowContent } from './SpecsDimensionsRowContent';
import { RangeRowContent } from './RangeRowContent';
import { AboutSupplierRowContent } from './AboutSupplierRowContent';
import { AwardsRowContent } from './AwardsRowContent';
import { DownloadsRowContent } from './DownloadsRowContent';
import {
    SpecificationRepresentativeRowContent,
    type SpecificationRep,
} from './SpecificationRepresentativeRowContent';
import { InstallersRowContent } from './InstallersRowContent';
import { SimilarProductsRowContent } from './SimilarProductsRowContent';

/** The CMS LibraryTab whose content is the specifications table. */
const SPECIFICATIONS_TAB_TITLE = 'Specifications';

/**
 * CMS tabs that duplicate built-in accordion rows: "Specifications" feeds the
 * Specs & Dimensions row, and "Product Range" duplicates the built-in
 * "Other Products in this range" carousel. Compared case-insensitively.
 */
const EXCLUDED_TAB_TITLES = [
    SPECIFICATIONS_TAB_TITLE.toLowerCase(),
    'product range',
];
const isExcludedTab = (title: string) =>
    EXCLUDED_TAB_TITLES.includes(title.trim().toLowerCase());

/** "A, B, C +4 more" — keeps spec-table values one line even with 99 colours. */
const joinWithMore = (values: string[], max = 3): string =>
    values.length <= max
        ? values.join(', ')
        : `${values.slice(0, max).join(', ')} +${values.length - max} more`;

export interface ProductAccordionRows {
    rows: NumberedAccordionRow[];
    defaultOpenTitles: string[] | undefined;
}

/**
 * Builds the numbered spec-accordion rows from the product (Figma 1846:9004 /
 * 9046 / 9068 / 9088). Shared so the mobile standalone Similar Products section
 * can read `rows.length` to continue the "(NN)" numbering — the numbered
 * sequence is exactly these accordion rows.
 *
 * About the Supplier and Installers live inside the accordion on both
 * breakpoints; Similar Products joins them on desktop (default open) but stays
 * a standalone mobile section, so on mobile it is absent here and continues
 * the numbering itself.
 */
export const useProductAccordionRows = (): ProductAccordionRows => {
    const { product, similarProducts } = useLoaderData<typeof loader>();
    const { attributeMap } = useVariantOptionState();
    const { openEnquiryModal } = useProductEnquiryModal();
    const { mobile } = useAppDisplayModeContext();
    // Gates the Get-in-Touch fallback row: the enquiry prompt renders nothing
    // when the professional takes no enquiries and has no website, and an
    // empty accordion row must not be pushed.
    const { showEnquiryCTA } = useEnquiryVisibility(product.Professional);
    const hasWebsite = Boolean(product.Website || product.Professional.Website);

    const rows: NumberedAccordionRow[] = [];

    // (01) Product Details — thumbnail + spec table (Figma 1846:9004), with the
    // scope-of-use copy following full-width.
    const category = product.Categories?.[0];
    const rangeTitle = product.LibraryRange?.Title;
    const colourLabels = (
        attributeMap.get(ProductAttributeEnum.Colour)?.options ?? []
    ).map((option) => option.Label);
    const materialLabels = (
        attributeMap.get(ProductAttributeEnum.Material)?.options ?? []
    ).map((option) => option.Label);
    const sizeLabels = (product.Sizes ?? []).map((size) => size.FullTitle);
    const facets = groupApprovedFacetOptions(product.FacetOptions ?? []);
    // No "Product" name row — the title heads the row's description column.
    const detailItems: ProductInfoItem[] = [
        {
            label: 'Sold by',
            value: product.Professional?.Title ?? '',
            link: product.Professional?.Link,
        },
        {
            label: 'Category',
            value: category?.PrettyTitle ?? '',
            link:
                category && product.Professional
                    ? getProductCategoryUrl(product.Professional, category)
                    : null,
            newTab: true,
        },
        {
            label: 'Range',
            value: rangeTitle ?? '',
            link:
                rangeTitle && product.Professional
                    ? getProductRangeUrl(product.Professional, rangeTitle)
                    : null,
            newTab: true,
        },
        { label: 'Colour', value: joinWithMore(colourLabels) },
        { label: 'Material', value: joinWithMore(materialLabels) },
        { label: 'Size', value: joinWithMore(sizeLabels) },
        { label: 'Designer', value: product.Designer ?? '' },
        { label: 'Brand', value: product.Brand ?? '' },
        { label: 'Year', value: product.Year ?? '' },
        ...facets.map((facet) => ({
            label: facet.Label,
            value: facet.options.join(', '),
        })),
    ].filter((item) => item.value);
    const defaultImageSrc =
        product.DefaultImage?.LibraryImageFile?.DetailPageSquare?.Desktop;
    const fallbackImage = product.LibraryImages?.[0];
    const thumbnailSrc =
        defaultImageSrc ??
        fallbackImage?.LibraryImageFile?.DetailPageSquare?.Desktop;
    // Clear-cut/multiplied is a per-image flag, so read it off whichever image
    // supplied the src above — the thumbnail then gets the same contain +
    // multiply treatment the hero gallery gives that image, and photographs
    // keep their unblended cover crop.
    const thumbnailIsMultiplied = Boolean(
        defaultImageSrc
            ? product.DefaultImage?.IsMultiplied
            : fallbackImage?.IsMultiplied,
    );
    // B3: ImageBackgroundColor is already fetched via ProductImageFragment on
    // LibraryImages. Use the first LibraryImage's colour as the thumbnail
    // background (the same image whose src is the fallback above). When
    // DefaultImage is used instead, LibraryImages[0]'s colour is the closest
    // available approximation without extending the DefaultImage query fields.
    const thumbnailBgColor =
        product.LibraryImages?.[0]?.LibraryImageFile?.ImageBackgroundColor;
    // Custom CMS tabs (e.g. "Motion technologies", "Programme") fold into the
    // Product Details row as labelled rich-text sections rather than each
    // becoming its own numbered accordion row.
    const customSections = (product.LibraryTabs ?? [])
        .filter((tab) => !isExcludedTab(tab.Title) && tab.Content)
        .map((tab) => ({ title: tab.Title, html: tab.Content as string }));
    if (
        product.Content ||
        product.ScopeOfUseDescription ||
        detailItems.length ||
        customSections.length
    ) {
        rows.push({
            title: 'Product Details',
            // The hero's clamped excerpt anchors down to this row. The id
            // lives on the AccordionItem (always mounted, unlike the row
            // content) so the link still lands when the row is closed;
            // scroll-mt keeps the landing clear of the viewport edge.
            id: PRODUCT_DETAILS_ANCHOR_ID,
            // Let the open row's content overflow on desktop so the left
            // thumbnail can stick as the (potentially very long) custom CMS
            // sections scroll past. Reverts to clipped while collapsing.
            itemClassName:
                'scroll-mt-24 md:data-[state=open]:[&_.accordion-content]:overflow-visible',
            content: (
                <ProductDetailsRowContent
                    title={product.Title}
                    descriptionHtml={product.Content}
                    image={
                        thumbnailSrc
                            ? {
                                  src: thumbnailSrc,
                                  alt: product.Title,
                                  isMultiplied: thumbnailIsMultiplied,
                              }
                            : undefined
                    }
                    imageBackgroundColor={thumbnailBgColor}
                    items={detailItems}
                    scopeOfUseHtml={product.ScopeOfUseDescription}
                    customSections={customSections}
                />
            ),
        });
    }

    // (02) Specs & Dimensions — diagram + Dimensions / Specs / Performance /
    // Warranty table (Figma 1846:9046). The CMS "Specifications" tab becomes the
    // Specs row.
    const specificationsTab = (product.LibraryTabs ?? []).find(
        (tab) => tab.Title === SPECIFICATIONS_TAB_TITLE && tab.Content,
    );
    // One size per line (Figma 2354:59109), rendered with whitespace-pre-line.
    const dimensions = sizeLabels.join('\n');
    const diagrams = product.DiagramImages ?? [];
    if (
        dimensions ||
        specificationsTab?.Content ||
        product.PerformanceDescription ||
        product.WarrantyDuration ||
        diagrams.length > 0
    ) {
        rows.push({
            title: 'Specs & Dimensions',
            content: (
                <SpecsDimensionsRowContent
                    diagrams={diagrams}
                    productId={product.ID}
                    productTitle={product.Title}
                    professionalTitle={product.Professional?.Title ?? ''}
                    onEnquire={() =>
                        openEnquiryModal({
                            title: 'Enquire',
                            ctaLabel: 'Enquire',
                            messageSubject: `a dimension diagram for ${product.Title}`,
                        })
                    }
                    dimensions={dimensions}
                    specsHtml={specificationsTab?.Content}
                    performanceHtml={product.PerformanceDescription}
                    warranty={product.WarrantyDuration ?? undefined}
                />
            ),
        });
    }

    // (03) Downloads & Documentation — inline gated file list. Always shown:
    // every product offers the auto-generated ArchiPro tear sheet even when the
    // supplier has uploaded no files of its own.
    rows.push({
        title: 'Downloads & Documentation',
        content: <DownloadsRowContent />,
    });

    // (04) Specification Representative — the supplier's team members flagged as
    // specification reps (Figma 2354:59152). Closed by default and only pushed
    // when there is at least one rep, so numbering stays stable.
    const professional = product.Professional;
    const specificationReps: SpecificationRep[] = (
        professional?.SpecificationReps?.edges ?? []
    ).map(({ node }) => ({
        id: node.ID,
        name: node.Name,
        // Respect the member's "Show member position on Profile" permission.
        role: node.HideJobTitle || !node.JobTitle ? '' : node.JobTitle,
        bio: node.Summary ? stripHtml(node.Summary) : '',
        // Respect the member's "Show phone number on Profile" permission; when
        // hidden (or absent) the card falls back to a Send Message CTA.
        phone: node.HidePhone || !node.Phone ? null : node.Phone,
        image:
            node.MemberImage?.TeamMemberDisplay ??
            node.MemberImage?.Avatar ??
            undefined,
    }));
    if (specificationReps.length > 0) {
        const bannerSrc =
            professional?.SpecificationRepBanner?.BannerImage?.LibraryImageFile
                ?.DetailBannerStandard?.[mobile ? 'Mobile' : 'Desktop'];
        rows.push({
            title: 'Specification Representative',
            content: (
                <SpecificationRepresentativeRowContent
                    reps={specificationReps}
                    onSendMessage={() => openEnquiryModal()}
                    professionalLink={professional?.Link}
                    brandImage={
                        bannerSrc
                            ? {
                                  src: bannerSrc,
                                  alt: professional?.Title ?? '',
                              }
                            : undefined
                    }
                    brandLogo={
                        professional?.CompanyLogo
                            ? {
                                  src: professional.CompanyLogo,
                                  background: professional.LogoBackgroundColor,
                                  alt: professional.Title ?? '',
                              }
                            : undefined
                    }
                />
            ),
        });
    } else if (!mobile && (showEnquiryCTA || hasWebsite)) {
        // No specification representative set up — the enquiry hook fills the
        // same slot, so the accordion always carries a mid-page contact
        // moment. Mobile keeps its standalone hook card after the accordion
        // instead.
        rows.push({
            title: 'Get in Touch',
            content: (
                <ProductEnquiryPrompt
                    showPhone
                    heading="Have a question about this product?"
                    body="The team at {supplier} can help with pricing, availability and lead times."
                />
            ),
        });
    }

    if (product.Awards.length > 0) {
        rows.push({
            title: 'Awards and Recognition',
            content: <AwardsRowContent awards={product.Awards} />,
        });
    }

    // The product range lives inside the accordion as a 4x2 tile grid — the
    // giant range title, category pills and View Range CTA were dropped; the
    // "See more products in {range}" link moved to the Magic Island.
    const rangeTiles = (product.ProductRange?.products ?? []) as DetailTile[];
    if (rangeTiles.some((tile) => tile.images?.[0]?.src)) {
        rows.push({
            title: 'Other Products in this range',
            content: (
                <RangeRowContent
                    tiles={rangeTiles}
                    rangeTitle={rangeTitle ?? product.Title}
                    rangeTo={
                        rangeTitle && product.Professional
                            ? getProductRangeUrl(
                                  product.Professional,
                                  rangeTitle,
                              )
                            : undefined
                    }
                />
            ),
        });
    }

    // About the Supplier (Figma 1841:10484) and Installers live inside the
    // accordion on both breakpoints; only Similar Products (Figma 1841:9733)
    // stays a standalone section on mobile. Both rows force-mount on mobile:
    // single mode leaves them closed by default, and the supplier / installer
    // names and links must stay in the HTML that mobile-first indexing crawls.
    if (product.Professional) {
        rows.push({
            title: 'About the Supplier',
            forceMount: mobile,
            // The products carousel bleeds full-width via a negative margin
            // (mobile always; desktop above 1920, where it breaks out of the
            // capped accordion); let the open row escape the accordion's
            // overflow-hidden clip so the bleed reaches the screen edge
            // instead of being cut at the padded content box. Reverts to
            // clipped while collapsing.
            itemClassName:
                'max-md:data-[state=open]:[&_.accordion-content]:overflow-visible qhd:data-[state=open]:[&_.accordion-content]:overflow-visible',
            content: <AboutSupplierRowContent />,
        });
    }
    const hasInstallers = (product.Installers?.professionals ?? []).some(
        (professional) => professional.image?.src,
    );
    if (hasInstallers) {
        rows.push({
            title: 'Installers',
            forceMount: mobile,
            // Same escape as About the Supplier: the installers rail breaks
            // out of the capped accordion above 1920.
            itemClassName:
                'max-md:data-[state=open]:[&_.accordion-content]:overflow-visible qhd:data-[state=open]:[&_.accordion-content]:overflow-visible',
            content: <InstallersRowContent />,
        });
    }
    // Gated on the SSR'd first page so the row only appears (and is numbered)
    // when there are products — no empty row, stable numbering.
    if (!mobile && (similarProducts?.products.length ?? 0) > 0) {
        rows.push({
            title: 'Similar Products',
            content: <SimilarProductsRowContent />,
        });
    }

    // Desktop opens Product Details + About the Supplier + Similar Products on
    // load. Mobile (single mode) opens Product Details only: the full product
    // description lives in that row and Radix unmounts closed content — with
    // the row closed, the description would be missing from the mobile HTML
    // that mobile-first indexing crawls.
    const defaultOpenTitles = mobile
        ? ['Product Details']
        : ['Product Details', 'About the Supplier', 'Similar Products'];

    return { rows, defaultOpenTitles };
};
